import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, fmtAmount as fmt, unwrap } from "../api";
import {
  Button,
  DeleteButton,
  Empty,
  ErrorNote,
  Loading,
  Panel,
  Table,
  TableSearch,
  Td,
  Th,
  useTable,
} from "../ui";
import { useDeleteRecord } from "../useDeleteRecord";
import { useResource } from "../useResource";
import { ExpenseForm, type ExpenseFormInitial } from "./shared";

async function listExpenses(slug: string) {
  return unwrap(await api.api.c[":slug"].expenses.$get({ param: { slug }, query: {} }));
}
async function listTemplates(slug: string) {
  return unwrap(await api.api.c[":slug"].expenses.templates.$get({ param: { slug } }));
}

type ExpenseRow = Awaited<ReturnType<typeof listExpenses>>["expenses"][number];
type TemplateRow = Awaited<ReturnType<typeof listTemplates>>["templates"][number];

function expenseToInitial(e: ExpenseRow): ExpenseFormInitial {
  return {
    scope: e.scope,
    projectId: e.projectId,
    costCodeId: e.costCodeId,
    text: e.description,
    amount: e.amount,
    date: e.incurredOn,
  };
}

function templateToInitial(t: TemplateRow): ExpenseFormInitial {
  return {
    scope: t.scope,
    projectId: t.projectId,
    costCodeId: t.costCodeId,
    text: t.name,
    amount: t.amount,
    date: t.anchorDate,
    frequency: t.frequency,
  };
}

export function Expenses({
  slug,
  currency,
  role,
}: { slug: string; currency: string; role: string }) {
  // undefined = closed, null = adding, a row = editing that one.
  const [oneOffForm, setOneOffForm] = useState<ExpenseRow | null | undefined>(undefined);
  const [recurringForm, setRecurringForm] = useState<TemplateRow | null | undefined>(undefined);
  const del = useDeleteRecord(slug);

  const costCodes = useQuery({
    queryKey: ["cost-codes", slug],
    queryFn: async () => unwrap(await api.api.c[":slug"]["cost-codes"].$get({ param: { slug } })),
  });
  const projects = useQuery({
    queryKey: ["projects", slug],
    queryFn: async () => unwrap(await api.api.c[":slug"].projects.$get({ param: { slug } })),
  });
  const summary = useQuery({
    queryKey: ["expenses-by-cost-code", slug],
    queryFn: async () =>
      unwrap(
        await api.api.c[":slug"].expenses["by-cost-code"].$get({ param: { slug }, query: {} }),
      ),
  });

  const expenses = useResource({
    key: ["expenses", slug],
    list: () => listExpenses(slug),
    create: async (body: Record<string, unknown>) =>
      unwrap(await api.api.c[":slug"].expenses.$post({ param: { slug }, json: body as never })),
    update: async (id: string, body: Record<string, unknown>) =>
      unwrap(
        await api.api.c[":slug"].expenses[":id"].$patch({
          param: { slug, id },
          json: body as never,
        }),
      ),
    alsoInvalidate: [["expenses-by-cost-code", slug]],
  });

  const templates = useResource({
    key: ["expense-templates", slug],
    list: () => listTemplates(slug),
    create: async (body: Record<string, unknown>) =>
      unwrap(
        await api.api.c[":slug"].expenses.templates.$post({ param: { slug }, json: body as never }),
      ),
    update: async (id: string, body: Record<string, unknown>) =>
      unwrap(
        await api.api.c[":slug"].expenses.templates[":id"].$patch({
          param: { slug, id },
          json: body as never,
        }),
      ),
  });

  const codeOptions = costCodes.data?.costCodes ?? [];
  const projectOptions = projects.data?.projects ?? [];

  const expensesTable = useTable(expenses.query.data?.expenses ?? [], {
    filter: (e, q) =>
      `${e.description} ${e.costCode} ${e.costCodeName} ${e.projectName ?? ""}`
        .toLowerCase()
        .includes(q.toLowerCase()),
    sorters: {
      date: (e) => e.incurredOn,
      description: (e) => e.description.toLowerCase(),
      costCode: (e) => e.costCode.toLowerCase(),
      against: (e) => (e.scope === "project" ? (e.projectName ?? "") : "Company").toLowerCase(),
      amount: (e) => Number(e.amount),
    },
  });

  const templatesTable = useTable(templates.query.data?.templates ?? [], {
    filter: (t, q) =>
      `${t.name} ${t.frequency} ${t.projectName ?? ""}`.toLowerCase().includes(q.toLowerCase()),
    sorters: {
      name: (t) => t.name.toLowerCase(),
      every: (t) => t.frequency,
      against: (t) => (t.scope === "project" ? (t.projectName ?? "") : "Company").toLowerCase(),
      lastGenerated: (t) => t.lastGeneratedPeriod ?? "",
      amount: (t) => Number(t.amount),
    },
  });

  const costCodeSummaryTable = useTable(summary.data?.costCodes ?? [], {
    filter: (r, q) => `${r.code} ${r.name}`.toLowerCase().includes(q.toLowerCase()),
    sorters: {
      code: (r) => r.code.toLowerCase(),
      name: (r) => r.name.toLowerCase(),
      projects: (r) => Number(r.projectTotal),
      company: (r) => Number(r.companyTotal),
      total: (r) => Number(r.total),
    },
  });

  return (
    <>
      <Panel
        title="Expenses"
        action={
          <Button
            onClick={() => {
              expenses.clearErrors();
              setOneOffForm(oneOffForm === undefined ? null : undefined);
            }}
          >
            {oneOffForm === undefined ? "Record expense" : "Cancel"}
          </Button>
        }
      >
        {oneOffForm !== undefined && (
          <ExpenseForm
            // Keyed by row, so switching which expense is being edited (or to
            // "new") doesn't inherit the previous one's half-typed values.
            key={oneOffForm?.id ?? "new"}
            currency={currency}
            codes={codeOptions}
            projects={projectOptions}
            initial={oneOffForm ? expenseToInitial(oneOffForm) : undefined}
            errors={expenses.fieldErrors}
            formError={expenses.formError}
            saving={expenses.saving}
            submitLabel={oneOffForm ? "Save changes" : "Record expense"}
            onSubmit={async (body) => {
              if (oneOffForm) await expenses.update({ id: oneOffForm.id, body });
              else await expenses.create(body);
              setOneOffForm(undefined);
            }}
          />
        )}

        {del.error && <ErrorNote>{del.error}</ErrorNote>}

        {expenses.query.isPending ? (
          <Loading />
        ) : expenses.query.isError ? (
          <ErrorNote>{(expenses.query.error as Error).message}</ErrorNote>
        ) : expenses.query.data.expenses.length === 0 ? (
          <Empty title="No expenses yet">
            Record a one-off cost, or set up a recurring one below.
          </Empty>
        ) : (
          <>
            <TableSearch
              value={expensesTable.query}
              onChange={expensesTable.setQuery}
              placeholder="Filter expenses…"
            />
            <Table
              head={
                <>
                  <Th
                    sortKey="date"
                    active={expensesTable.sortKey === "date"}
                    dir={expensesTable.dir}
                    onSort={expensesTable.toggleSort}
                  >
                    Date
                  </Th>
                  <Th
                    sortKey="description"
                    active={expensesTable.sortKey === "description"}
                    dir={expensesTable.dir}
                    onSort={expensesTable.toggleSort}
                  >
                    Description
                  </Th>
                  <Th
                    sortKey="costCode"
                    active={expensesTable.sortKey === "costCode"}
                    dir={expensesTable.dir}
                    onSort={expensesTable.toggleSort}
                  >
                    Cost code
                  </Th>
                  <Th
                    sortKey="against"
                    active={expensesTable.sortKey === "against"}
                    dir={expensesTable.dir}
                    onSort={expensesTable.toggleSort}
                  >
                    Against
                  </Th>
                  <Th
                    right
                    sortKey="amount"
                    active={expensesTable.sortKey === "amount"}
                    dir={expensesTable.dir}
                    onSort={expensesTable.toggleSort}
                  >
                    Amount
                  </Th>
                  <Th />
                </>
              }
            >
              {expensesTable.rows.map((e) => (
                <tr
                  key={e.id}
                  className="border-b border-neutral-100 even:bg-neutral-50 transition-colors hover:bg-neutral-100"
                >
                  <Td mono muted>
                    {e.incurredOn}
                  </Td>
                  <Td>
                    {e.description}
                    {e.templateId && (
                      <span className="ml-2 rounded-full bg-neutral-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-neutral-500">
                        recurring
                      </span>
                    )}
                    {e.status === "void" && (
                      <span className="ml-2 text-xs text-red-600">voided</span>
                    )}
                  </Td>
                  <Td muted>
                    {e.costCode} {e.costCodeName}
                  </Td>
                  {/* The project/company split, stated rather than inferred. */}
                  <Td muted>{e.scope === "project" ? (e.projectName ?? "—") : "Company"}</Td>
                  <Td right mono>
                    {fmt(e.amount, e.currency)}
                  </Td>
                  <Td right>
                    <div className="flex justify-end gap-1">
                      {/* A voided or template-generated row can't be edited — the
                          server rejects both, so don't offer the button. */}
                      {e.status !== "void" && !e.templateId && (
                        <Button
                          variant="ghost"
                          onClick={() => {
                            expenses.clearErrors();
                            setOneOffForm(e);
                          }}
                        >
                          Edit
                        </Button>
                      )}
                      {role === "owner" && (
                        <DeleteButton
                          busy={del.deleting}
                          onConfirm={() => del.remove("expense", e.id)}
                        />
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
            </Table>
          </>
        )}
      </Panel>

      <Panel
        title="Recurring expenses"
        action={
          <Button
            onClick={() => {
              templates.clearErrors();
              setRecurringForm(recurringForm === undefined ? null : undefined);
            }}
          >
            {recurringForm === undefined ? "New recurring expense" : "Cancel"}
          </Button>
        }
      >
        <p className="mb-3 max-w-prose text-sm text-neutral-500">
          These are rules, not charges. A nightly job creates the actual expense for each period,
          backfilling any it missed — so a run that fails doesn't lose a month.
        </p>

        {recurringForm !== undefined && (
          <ExpenseForm
            recurring
            key={recurringForm?.id ?? "new"}
            currency={currency}
            codes={codeOptions}
            projects={projectOptions}
            initial={recurringForm ? templateToInitial(recurringForm) : undefined}
            errors={templates.fieldErrors}
            formError={templates.formError}
            saving={templates.saving}
            submitLabel={recurringForm ? "Save changes" : "Create recurring expense"}
            onSubmit={async (body) => {
              if (recurringForm) await templates.update({ id: recurringForm.id, body });
              else await templates.create(body);
              setRecurringForm(undefined);
            }}
          />
        )}

        {templates.query.isPending ? (
          <Loading />
        ) : templates.query.data && templates.query.data.templates.length > 0 ? (
          <>
            <TableSearch
              value={templatesTable.query}
              onChange={templatesTable.setQuery}
              placeholder="Filter recurring expenses…"
            />
            <Table
              head={
                <>
                  <Th
                    sortKey="name"
                    active={templatesTable.sortKey === "name"}
                    dir={templatesTable.dir}
                    onSort={templatesTable.toggleSort}
                  >
                    Name
                  </Th>
                  <Th
                    sortKey="every"
                    active={templatesTable.sortKey === "every"}
                    dir={templatesTable.dir}
                    onSort={templatesTable.toggleSort}
                  >
                    Every
                  </Th>
                  <Th
                    sortKey="against"
                    active={templatesTable.sortKey === "against"}
                    dir={templatesTable.dir}
                    onSort={templatesTable.toggleSort}
                  >
                    Against
                  </Th>
                  <Th
                    sortKey="lastGenerated"
                    active={templatesTable.sortKey === "lastGenerated"}
                    dir={templatesTable.dir}
                    onSort={templatesTable.toggleSort}
                  >
                    Last generated
                  </Th>
                  <Th
                    right
                    sortKey="amount"
                    active={templatesTable.sortKey === "amount"}
                    dir={templatesTable.dir}
                    onSort={templatesTable.toggleSort}
                  >
                    Amount
                  </Th>
                  <Th />
                </>
              }
            >
              {templatesTable.rows.map((t) => (
                <tr
                  key={t.id}
                  className="border-b border-neutral-100 even:bg-neutral-50 transition-colors hover:bg-neutral-100"
                >
                  <Td>
                    {t.name}
                    {!t.isActive && <span className="ml-2 text-xs text-neutral-500">paused</span>}
                  </Td>
                  <Td muted>
                    {t.intervalCount && t.intervalCount > 1 ? `${t.intervalCount} × ` : ""}
                    {t.frequency}
                  </Td>
                  <Td muted>{t.scope === "project" ? (t.projectName ?? "—") : "Company"}</Td>
                  <Td mono muted>
                    {t.lastGeneratedPeriod ?? "never"}
                  </Td>
                  <Td right mono>
                    {fmt(t.amount, t.currency)}
                  </Td>
                  <Td right>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        templates.clearErrors();
                        setRecurringForm(t);
                      }}
                    >
                      Edit
                    </Button>
                  </Td>
                </tr>
              ))}
            </Table>
          </>
        ) : (
          <Empty title="Nothing recurring yet">Rent, software, insurance — anything fixed.</Empty>
        )}
      </Panel>

      <Panel title="Spend by cost code">
        {summary.isPending ? (
          <Loading />
        ) : summary.data && summary.data.costCodes.some((r) => r.entries > 0) ? (
          <>
            <TableSearch
              value={costCodeSummaryTable.query}
              onChange={costCodeSummaryTable.setQuery}
              placeholder="Filter cost codes…"
            />
            <Table
              head={
                <>
                  <Th
                    sortKey="code"
                    active={costCodeSummaryTable.sortKey === "code"}
                    dir={costCodeSummaryTable.dir}
                    onSort={costCodeSummaryTable.toggleSort}
                  >
                    Code
                  </Th>
                  <Th
                    sortKey="name"
                    active={costCodeSummaryTable.sortKey === "name"}
                    dir={costCodeSummaryTable.dir}
                    onSort={costCodeSummaryTable.toggleSort}
                  >
                    Name
                  </Th>
                  <Th
                    right
                    sortKey="projects"
                    active={costCodeSummaryTable.sortKey === "projects"}
                    dir={costCodeSummaryTable.dir}
                    onSort={costCodeSummaryTable.toggleSort}
                  >
                    Projects
                  </Th>
                  <Th
                    right
                    sortKey="company"
                    active={costCodeSummaryTable.sortKey === "company"}
                    dir={costCodeSummaryTable.dir}
                    onSort={costCodeSummaryTable.toggleSort}
                  >
                    Company
                  </Th>
                  <Th
                    right
                    sortKey="total"
                    active={costCodeSummaryTable.sortKey === "total"}
                    dir={costCodeSummaryTable.dir}
                    onSort={costCodeSummaryTable.toggleSort}
                  >
                    Total
                  </Th>
                </>
              }
            >
              {costCodeSummaryTable.rows.map((r) => (
                <tr
                  key={r.costCodeId}
                  className="border-b border-neutral-100 even:bg-neutral-50 transition-colors hover:bg-neutral-100"
                >
                  <Td mono>{r.code}</Td>
                  <Td muted>{r.name}</Td>
                  <Td right mono muted>
                    {fmt(r.projectTotal, currency)}
                  </Td>
                  <Td right mono muted>
                    {fmt(r.companyTotal, currency)}
                  </Td>
                  <Td right mono>
                    {fmt(r.total, currency)}
                  </Td>
                </tr>
              ))}
            </Table>
          </>
        ) : (
          <Empty title="Nothing to summarise yet" />
        )}
      </Panel>
    </>
  );
}
